/* eslint-disable @typescript-eslint/member-delimiter-style */
import React, { useState, useEffect } from 'react'
import { CircularProgress, IconButton } from '@material-ui/core'
import GetAppIcon from '@material-ui/icons/GetApp'

import { Moment } from 'config/moment'
import { useApiCall } from 'hooks/useApi'
import { DonutTagChart } from 'features/Main/NewDashboard/components/DonutTagChart'
import { formatDonutChartData } from 'utils/chart'

import { useLoadingTracker } from '../../hooks/useLoadingTracker'
import { getNewVsReturningUsers } from './api'
import CustomTooltip from '../../components/CustomTooltip'

const montserratStyle = { fontFamily: 'Montserrat, sans-serif', color: '#1E1E1E' }

const downloadCSV = (data: Array<{ name: string; value: number }>) => {
  const csvRows = [['Tipo', 'Porcentaje'], ...data.map(row => [row.name, row.value])]
  const csvContent = csvRows.map(e => e.join(',')).join('\n')
  const blob = new Blob([csvContent], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)

  const a = document.createElement('a')
  a.href = url
  a.download = 'usuarios_nuevos_vs_recurrentes.csv'
  a.click()
  URL.revokeObjectURL(url)
}

interface NewVsReturningProps {
  period1: { startDate: Moment; endDate: Moment }
  trigger: number
  storeIds?: number[]
  // eslint-disable-next-line react/require-default-props
  onLoaded?: () => void
}

interface NewVsReturningParams {
  startDate: string
  endDate: string
}

interface NewVsReturningResponse {
  data: Array<{
    name: string
    value: number
    quantity: number
  }>
}

export const NewVsReturning: React.FC<NewVsReturningProps> = ({
  period1,
  trigger,
  storeIds,
  onLoaded,
}) => {
  const [data, setData] = useState<Array<{ name: string; value: number; quantity: number }>>([])
  const [dataWithColors, setDataWithColors] = useState<
    Array<{ name: string; value: number; color: string; quantity?: number }>
  >([])
  const [statsCall, statsLoading] = useApiCall<NewVsReturningParams, NewVsReturningResponse>(
    getNewVsReturningUsers,
  )

  // Track loading state
  useLoadingTracker({ isLoading: statsLoading, componentId: 'NewVsReturning' })

  const fetchData = async () => {
    try {
      const startDate = period1.startDate.format('YYYY-MM-DD')
      const endDate = period1.endDate.format('YYYY-MM-DD')

      const response = await statsCall({
        startDate,
        endDate,
        ...(storeIds && storeIds.length > 0 && { storeIds: storeIds.join(',') }),
      })
      if (response?.data) {
        setData(response.data)
      }
    } catch (error) {
      console.error('Error fetching new vs returning users:', error)
    } finally {
      if (onLoaded) {
        onLoaded()
      }
    }
  }

  useEffect(() => {
    if (trigger > 0) {
      fetchData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger])

  useEffect(() => {
    const formattedData = formatDonutChartData(
      data.map((item, idx) => ({
        ...item,
        color: idx === 0 ? 'sky' : 'teal',
        quantity: item.quantity || 0, // Incluir cantidad absoluta para el tooltip
      })),
    )
    console.log('formattedData', formattedData)
    setDataWithColors(formattedData)
  }, [data])

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex flex-row justify-between items-start mb-2">
        <span className="font-bold text-base block mb-4" style={montserratStyle}>
          Usuarios nuevos vs. usuarios recurrentes
        </span>
        <IconButton
          disabled={statsLoading}
          className="ml-2"
          aria-label="download"
          onClick={() => downloadCSV(data)}
        >
          <GetAppIcon />
        </IconButton>
      </div>
      {statsLoading ? (
        <div className="flex justify-center items-center h-full">
          <CircularProgress />
        </div>
      ) : (
        <DonutTagChart data={dataWithColors} customTooltip={CustomTooltip} />
      )}
    </div>
  )
}

NewVsReturning.defaultProps = {
  storeIds: undefined,
}
